import { Link } from "wouter";
import { ArrowLeft, Flame, Heart, Trophy, Sparkles, AlertTriangle } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getLoginUrl } from "@/const";
import { PageChrome } from "@/components/tabs/PageChrome";
import { GrowthSection } from "@/components/GrowthSection";

export default function Profile() {
  const { user, isAuthenticated, loading } = useAuth();
  const { t, locale } = useI18n();

  const streakQuery = trpc.streak.me.useQuery(undefined, { enabled: isAuthenticated });
  const karmaQuery = trpc.karma.summary.useQuery(undefined, { enabled: isAuthenticated });
  const recentQuery = trpc.profile.recentReceipts.useQuery(
    { limit: 8 },
    { enabled: isAuthenticated },
  );
  // Full chronological history of approved weeklies for the
  // archive section. Capped at 200 server-side so this is bounded
  // even for long-tenured users.
  const weekliesQuery = trpc.profile.approvedWeeklies.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
  const teamsQuery = trpc.team.mine.useQuery(undefined, { enabled: isAuthenticated });
  const teamId = teamsQuery.data?.[0]?.team.id;
  const teamRole = teamsQuery.data?.[0]?.role;
  // Stale-blocker dashboard is a manager surface; the server enforces it via
  // assertTeamManagerOrOwner. Skip the query entirely for plain members so
  // we don't make a guaranteed-FORBIDDEN call on every page load.
  const isManagerOrOwner = teamRole === "owner" || teamRole === "manager";
  const blockersQuery = trpc.team.blockers.useQuery(
    { teamId: teamId!, hoursOld: 24, windowDays: 7 },
    { enabled: isAuthenticated && Boolean(teamId) && isManagerOrOwner },
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        …
      </div>
    );
  }
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">
          {locale === "ar" ? "يرجى تسجيل الدخول" : "Please sign in to view your profile."}
        </p>
        <a
          href={getLoginUrl()}
          className="px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium"
        >
          {locale === "ar" ? "تسجيل الدخول" : "Sign in"}
        </a>
      </div>
    );
  }

  const streak = streakQuery.data;
  const karma = karmaQuery.data ?? { heart: 0, fire: 0, clap: 0, thanks: 0, total: 0 };
  const recents = recentQuery.data ?? [];

  // Group approved weeklies by year-month for the archive section.
  // Keys are "YYYY-MM" so they sort chronologically; we render
  // descending so the newest month is up top.
  const weeklyArchive = (() => {
    const items = weekliesQuery.data ?? [];
    const groups = new Map<string, typeof items>();
    for (const r of items) {
      const key = r.periodKey.slice(0, 7); // YYYY-MM
      const arr = groups.get(key) ?? [];
      arr.push(r);
      groups.set(key, arr);
    }
    return Array.from(groups.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([month, rows]) => ({ month, rows }));
  })();

  return (
    <div className="min-h-screen bg-background">
      {/* Same chrome as the tabbed screens (AppTopBar + max-w-lg column);
          the bottom tab bar comes from TabShell via tabbed() in App.tsx. */}
      <PageChrome
        title={locale === "ar" ? "ملفي الشخصي" : "Profile"}
        backHref="/"
        backLabel={t.backToCanvas}
      >
        {/* Identity */}
        <div className="flex items-center gap-4 mb-10">
          <Avatar className="h-16 w-16">
            <AvatarFallback className="bg-primary text-primary-foreground text-xl">
              {user?.name?.[0]?.toUpperCase() ?? "?"}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-display text-3xl">{user?.name ?? ""}</div>
            <div className="text-sm text-muted-foreground">{user?.email ?? ""}</div>
          </div>
        </div>

        {/* Profile stats are USER-GLOBAL by design (your identity, not one
            workspace) — they do NOT change when you switch workspaces. Per-
            workspace numbers live on the Home tab. Label it so that's clear. */}
        <div
          className="text-xs text-muted-foreground mb-3"
          data-testid="profile-stats-scope-note"
        >
          {locale === "ar"
            ? "نشاطك الإجمالي عبر جميع مساحات العمل. لا يتغيّر عند تبديل المساحة."
            : "Your overall activity across all workspaces. This doesn’t change when you switch workspaces."}
        </div>

        {/* Stats grid */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          <StatTile
            icon={<Flame className="h-5 w-5 text-orange-500" />}
            label={locale === "ar" ? "تتابع حالي" : "Current streak"}
            value={streak?.currentWeeks ?? 0}
            suffix={locale === "ar" ? "أسبوع" : "wk"}
          />
          <StatTile
            icon={<Trophy className="h-5 w-5 text-amber-500" />}
            label={t.longestStreak}
            value={streak?.longestWeeks ?? 0}
            suffix={locale === "ar" ? "أسبوع" : "wk"}
          />
          <StatTile
            icon={<Sparkles className="h-5 w-5 text-rose-500" />}
            label={t.weeksApproved}
            value={recents.filter(r => r.kind === "weekly").length}
            suffix=""
          />
          <StatTile
            icon={<Heart className="h-5 w-5 text-red-500" />}
            label={t.karmaReceived}
            value={karma.total}
            suffix=""
          />
        </section>

        {/* Growth ledger — self-review / promotion case generator */}
        <GrowthSection />

        {/* Karma breakdown */}
        <section className="mb-10">
          <h2 className="text-sm font-medium text-muted-foreground mb-3">
            {t.karmaReceived}
          </h2>
          {karmaQuery.isError ? (
            <div className="rounded-2xl border border-red-200 bg-red-50/60 dark:bg-red-950/20 p-5 text-sm text-red-700 flex items-center justify-between gap-3">
              <span>{t.errorLoadingProfile}</span>
              <button
                onClick={() => karmaQuery.refetch()}
                className="text-sm underline hover:no-underline"
              >
                {t.retry}
              </button>
            </div>
          ) : karma.total === 0 ? (
            <div className="rounded-2xl border bg-card p-6 text-sm text-muted-foreground text-center">
              {t.profileEmptyKarma}
            </div>
          ) : (
            <div className="rounded-2xl border bg-card p-5 grid grid-cols-4 gap-4">
              <KarmaCount emoji="❤" count={karma.heart} label={locale === "ar" ? "حب" : "Heart"} />
              <KarmaCount emoji="🔥" count={karma.fire} label={locale === "ar" ? "روعة" : "Fire"} />
              <KarmaCount emoji="👏" count={karma.clap} label={locale === "ar" ? "تصفيق" : "Clap"} />
              <KarmaCount emoji="🙏" count={karma.thanks} label={locale === "ar" ? "شكراً" : "Thanks"} />
            </div>
          )}
        </section>

        {/* Stale team blockers (manager view) */}
        {teamId && (blockersQuery.data?.length ?? 0) > 0 && (
          <section className="mb-10">
            <h2 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-3 inline-flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" />
              {locale === "ar" ? "عوائق قديمة في الفريق" : "Stale team blockers (>24h)"}
            </h2>
            <ul className="space-y-2">
              {blockersQuery.data!.map((b, i) => (
                <li
                  key={`${b.userId}-${i}`}
                  className="rounded-xl border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/30 p-4 flex items-start gap-3"
                >
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-amber-900 dark:text-amber-200 truncate">
                      {b.userName}
                    </div>
                    <div className="text-sm text-amber-800 dark:text-amber-200/90 mt-0.5">{b.text}</div>
                    <div className="text-xs text-amber-700/70 dark:text-amber-300/70 mt-1">
                      {locale === "ar"
                        ? `منذ ${b.ageHours} ساعة · ${b.reportedDate}`
                        : `${b.ageHours}h old · ${b.reportedDate}`}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Recent receipts */}
        <section>
          <h2 className="text-sm font-medium text-muted-foreground mb-3">
            {t.recentReceipts}
          </h2>
          {recentQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">…</div>
          ) : recentQuery.isError ? (
            <div className="rounded-2xl border border-red-200 bg-red-50/60 dark:bg-red-950/20 p-5 text-sm text-red-700 flex items-center justify-between gap-3">
              <span>{t.errorLoadingProfile}</span>
              <button
                onClick={() => recentQuery.refetch()}
                className="text-sm underline hover:no-underline"
              >
                {t.retry}
              </button>
            </div>
          ) : recents.length === 0 ? (
            <div className="rounded-2xl border bg-card p-6 text-sm text-muted-foreground">
              {t.noRecentReceipts}
            </div>
          ) : (
            <ul className="space-y-2">
              {recents.map(r => {
                const c = (r.content as any) ?? {};
                const headline =
                  (typeof c.headline === "string" && c.headline) ||
                  (Array.isArray(c.highlights) && c.highlights[0]) ||
                  (locale === "ar" ? "ملخّص" : "Receipt");
                return (
                  <li
                    key={r.id}
                    className="rounded-xl border bg-card p-4 flex items-start gap-3"
                  >
                    <span
                      className={`shrink-0 mt-0.5 text-xs px-2 py-0.5 rounded-full ${
                        r.kind === "weekly"
                          ? "bg-amber-100 text-amber-900"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {r.kind === "weekly"
                        ? locale === "ar" ? "أسبوعي" : "Weekly"
                        : locale === "ar" ? "يومي" : "Daily"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium leading-snug truncate">
                        {headline}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {r.periodKey}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Weekly archive — every approved weekly receipt the user
            has, grouped by month. Each row links to that week on the
            dashboard via ?week=YYYY-MM-DD so the user can jump
            straight to it rather than chevron-clicking back through
            weeks. */}
        {weeklyArchive.length > 0 && (
          <section className="mt-10">
            <h2 className="text-sm font-medium text-muted-foreground mb-3">
              {locale === "ar"
                ? "أرشيف التقارير الأسبوعية"
                : "Weekly archive"}
            </h2>
            <div className="space-y-5">
              {weeklyArchive.map(g => {
                const [yy, mm] = g.month.split("-");
                const monthLabel = new Date(
                  Number(yy),
                  Number(mm) - 1,
                  1,
                ).toLocaleDateString(locale === "ar" ? "ar" : "en", {
                  month: "long",
                  year: "numeric",
                });
                return (
                  <div key={g.month}>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground/80 mb-1.5">
                      {monthLabel}
                    </div>
                    <ul className="space-y-1.5">
                      {g.rows.map(r => {
                        const c = (r.content as any) ?? {};
                        const headline =
                          (typeof c.headline === "string" && c.headline) ||
                          (Array.isArray(c.highlights) && c.highlights[0]) ||
                          (locale === "ar" ? "ملخّص الأسبوع" : "Weekly summary");
                        return (
                          <li key={r.id}>
                            <Link
                              href={`/?week=${r.periodKey}`}
                              className="rounded-lg border bg-card hover:border-coral/40 hover:bg-coral-soft/10 px-3 py-2 flex items-start gap-3 transition-colors"
                            >
                              <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums mt-0.5">
                                {r.periodKey}
                              </span>
                              <span className="flex-1 text-sm leading-snug break-words">
                                {headline}
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </PageChrome>
    </div>
  );
}

function StatTile({
  icon,
  label,
  value,
  suffix,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  suffix: string;
}) {
  return (
    <div className="rounded-2xl border bg-card p-4">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-2 font-display text-3xl tabular-nums">
        {value}
        <span className="text-base text-muted-foreground ms-1">{suffix}</span>
      </div>
    </div>
  );
}

function KarmaCount({
  emoji,
  count,
  label,
}: {
  emoji: string;
  count: number;
  label: string;
}) {
  return (
    <div className="text-center">
      <div className="text-3xl">{emoji}</div>
      <div className="font-display text-2xl tabular-nums mt-1">{count}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
